import {type ApolloError} from 'apollo-client';
import {produce} from 'immer';
import _ from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {
  type FetchArtifactCollectionsPartialQueryResult,
  type Maybe,
  type RegistryProjectsSearchFragmentFragment,
  type RegistrySearchResultFragmentFragment,
  useBatchRegistryViewerMembershipQuery,
  useFetchArtifactCollectionsPartialQuery,
  useFetchArtifactMembershipsPartialQuery,
  useFetchRegistryProjectsPartialQuery,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import {OrganizationMemberRole} from '../../MembersTable/util';
import {sliceArray} from '../../Pagination';
import {type ModifiedTag} from '../../TagAssignment';
import {RegistriesSearchContext} from '../common/RegistriesSearchContextProvider';
import {useRegistryAccountSettings} from '../common/RegistryAccountSettings';
import {
  type ArtifactCollectionMembershipType,
  type GQLArtifactCollectionMembershipConnectionType,
  type GQLArtifactCollectionMembershipType,
  type GQLArtifactCollectionSearchEdgeType,
  type GQLRegistryProjectViewerMembershipType,
  type RegistrySearchCollectionType,
} from '../common/types';
import {
  checkRegistryProject,
  getCollectionRegistryName,
  getGlobalRegistryType,
  type globalRegistryType,
  isViewerRegistryMemberFromQuery,
} from '../common/utils';
import {getAllRegistriesSearchResult} from './utils';

export const parseRegistrySearchCollections = (
  gqlCollections: GQLArtifactCollectionSearchEdgeType[]
): RegistrySearchCollectionType[] => {
  const collectionsInfo = [];
  for (const edge of gqlCollections) {
    const collection = edge.node;
    if (collection != null) {
      const createdAt = new Date(collection.createdAt);
      const lastUpdatedAt = new Date(edge.lastUpdatedAt);
      const tags: ModifiedTag[] = [];
      collection.tags.edges.forEach(tag => {
        tags.push({
          id: tag.node.id,
          name: tag.node.name,
          tagCategoryName: tag.node.tagCategoryName,
          attributes: tag.node.attributes,
        });
      });
      const project = collection.project;
      const collectionInfo = {
        entityName: project?.entityName ?? '',
        projectName: project?.name ?? '',
        registryName: getCollectionRegistryName(
          project?.name,
          collection.defaultArtifactType.name
        ),
        projectId: project?.id ?? '',
        collectionId: collection.id,
        artifactTypeName: collection.defaultArtifactType.name,
        collectionName: collection.name,
        createdAt: createdAt.toString(),
        description: collection.description ?? '',
        tags: tags,
        isPortfolio: true,
        lastUpdatedAt: lastUpdatedAt.toString(),
        initialMemberships: edge.artifactMemberships,
        isArtifactMatch: edge.artifactMatch,
        protectedArtifactAliases: project?.protectedArtifactAliases.map(
          alias => alias.alias
        ),
        registryIcon: {
          name: project?.icon?.name ?? '',
          color: project?.icon?.color ?? '',
        },
      };
      collectionsInfo.push(collectionInfo);
    }
  }
  return collectionsInfo;
};

export type FetchArtifactCollectionsResult = {
  currentPage: RegistrySearchCollectionType[];
  totalCollectionsLoaded: number;
  loading: boolean; // Whether collections are still being fetched
  refetch: () => void;
  page: number;
  onPageChange: (page: number) => void;
  hasNextPage: boolean;
  error?: ApolloError;
};

function getAllRegistriesCollectionsSearchResult(
  data: FetchArtifactCollectionsPartialQueryResult['data']
): Maybe<RegistrySearchResultFragmentFragment> {
  return data?.organization?.orgEntity?.artifactCollectionsSearch ?? null;
}

type ArtifactCollectionsHookProps = {
  orgName: string;
  pageSize: number;
};

export const useSearchablePaginatedRegistryArtifactCollections = ({
  orgName,
  pageSize,
}: ArtifactCollectionsHookProps): FetchArtifactCollectionsResult => {
  // TODO: parameterize this to reduce duplicated code
  const getSearchResult = getAllRegistriesCollectionsSearchResult;

  const {searchFilter} = useContext(RegistriesSearchContext);
  const [page, setPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<
    RegistrySearchCollectionType[]
  >([]);

  const {refetch, fetchMore, data, loading, error} =
    useFetchArtifactCollectionsPartialQuery({
      variables: {
        orgName,
        searchTerm: searchFilter,
        limit: 100,
        artifactsLimit: 0,
      },
      onCompleted: data => {
        if (searchFilter) {
          Analytics.track('Registry search interaction', {
            action: 'search collections',
            entityName: orgName,
            searchTerm: searchFilter,
            numResults: getSearchResult(data)?.edges.length ?? 0,
          });
        }
      },
      context: propagateErrorsContext(),
      // This updates data whenever fetchMore is called and results in
      // duplicate queries being made, so we need to make sure we don't
      // append the same data twice in updateQuery
      notifyOnNetworkStatusChange: true,
    });

  const fetchMoreCollections = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {
          orgName,
          limit: 100,
          cursor,
          searchTerm: searchFilter,
          artifactsLimit: 0,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          return produce(prev, draft => {
            const draftSearchResult = getSearchResult(draft);
            if (
              draftSearchResult &&
              draftSearchResult.pageInfo.endCursor !==
                searchResult.pageInfo?.endCursor
            ) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [fetchMore, orgName, searchFilter, getSearchResult]
  );

  const {edges, pageInfo} = useMemo(() => {
    return {
      edges: getSearchResult(data)?.edges ?? [],
      pageInfo: getSearchResult(data)?.pageInfo,
    };
  }, [data, getSearchResult]);

  useEffect(() => {
    // check if we need to fetch more data for the current page
    if (edges.length < (page + 1) * pageSize && pageInfo?.hasNextPage) {
      fetchMoreCollections(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more data, set the current page
      const currentCollections: RegistrySearchCollectionType[] =
        parseRegistrySearchCollections(sliceArray(edges, page, pageSize));
      setCurrentPage(currentCollections);
    }
  }, [edges, pageInfo, page, pageSize, fetchMoreCollections]);

  // reset the view to the first page if the search filter changes
  useEffect(() => {
    setPage(0);
  }, [searchFilter]);

  return {
    currentPage: currentPage,
    loading: loading,
    refetch: refetch,
    totalCollectionsLoaded: edges.length,
    onPageChange: setPage,
    page: page,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    error: error,
  };
};

const parseArtifactCollectionMemberships = (
  gqlArtifactCollectionMemberships: GQLArtifactCollectionMembershipType[]
): ArtifactCollectionMembershipType[] => {
  return _.compact(
    gqlArtifactCollectionMemberships.map(membership => {
      return membership == null
        ? null
        : {
            membershipId: membership.id,
            versionIndex: membership.versionIndex ?? undefined,
            tags:
              membership.artifact?.tags?.map(tag => tag as ModifiedTag) ?? [],
            aliases: membership.aliases.map(alias => alias.alias),
            createdAt: membership.createdAt,
            metadata: membership.artifact?.metadata ?? '',
            size: membership.artifact?.size ?? 0,
            usedCount: membership.artifact?.usedCount ?? 0,
            artifactId: membership.artifact?.id ?? '',
          };
    })
  );
};

export type FetchArtifactMembershipsResult = {
  data: ArtifactCollectionMembershipType[];
  loading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  setPageSize: (pageSize: number) => void;
  pageSize: number;
};

export const usePaginatedArtifactMemberships = ({
  artifactCollectionId,
  initialPageSize,
  searchTerm,
  initialMemberships,
}: {
  artifactCollectionId: string;
  initialPageSize: number;
  searchTerm?: string;
  initialMemberships?: GQLArtifactCollectionMembershipConnectionType;
}): FetchArtifactMembershipsResult => {
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const initialMembershipsData: ArtifactCollectionMembershipType[] =
    initialMemberships?.edges.length
      ? parseArtifactCollectionMemberships(
          initialMemberships?.edges.map(edge => edge.node)
        )
      : [];
  const initialMembershipsPageInfo = initialMemberships?.pageInfo;

  const {
    data: memberships,
    loading,
    fetchMore,
  } = useFetchArtifactMembershipsPartialQuery({
    variables: {
      collectionId: artifactCollectionId,
      limit: pageSize,
      searchTerm,
      after: initialMembershipsPageInfo?.endCursor,
    },
    // This updates data whenever fetchMore is called and results in
    // duplicate queries being made, so we need to make sure we don't
    // append the same data twice in updateQuery
    notifyOnNetworkStatusChange: true,
  });

  const fetchMoreArtifactMemberships = useCallback(() => {
    const cursor =
      memberships?.artifactCollection?.artifactMemberships.pageInfo.endCursor;
    fetchMore({
      variables: {
        collectionId: artifactCollectionId,
        limit: pageSize,
        searchTerm,
        after: cursor,
      },
      updateQuery: (prev: any, {fetchMoreResult}: {fetchMoreResult?: any}) => {
        const searchResult =
          fetchMoreResult?.artifactCollection?.artifactMemberships;
        if (!searchResult) {
          return prev;
        }
        return produce(
          prev,
          (draft: {
            artifactCollection: {
              artifactMemberships: {edges: any[]; pageInfo: any};
            };
          }) => {
            if (
              draft.artifactCollection &&
              draft.artifactCollection.artifactMemberships.pageInfo
                .endCursor !== searchResult.pageInfo.endCursor
            ) {
              draft.artifactCollection.artifactMemberships.edges.push(
                ...searchResult.edges
              );
              draft.artifactCollection.artifactMemberships.pageInfo =
                searchResult.pageInfo;
            }
          }
        );
      },
    });
  }, [
    memberships?.artifactCollection?.artifactMemberships.pageInfo.endCursor,
    fetchMore,
    artifactCollectionId,
    pageSize,
    searchTerm,
  ]);

  return {
    data: initialMembershipsData.concat(
      parseArtifactCollectionMemberships(
        memberships?.artifactCollection?.artifactMemberships.edges.map(
          edge => edge.node
        ) ?? []
      )
    ),
    loading: loading,
    fetchNextPage: fetchMoreArtifactMemberships,
    hasNextPage: memberships
      ? (memberships?.artifactCollection?.artifactMemberships.pageInfo
          ?.hasNextPage ?? false)
      : (initialMembershipsPageInfo?.hasNextPage ?? false),
    setPageSize: setPageSize,
    pageSize,
  };
};

function parseRegistriesSearchResult(
  orgName: string,
  orgId: string,
  gqlRegistries: RegistryProjectsSearchFragmentFragment['edges']
): globalRegistryType[] {
  const registriesInfo = [];
  for (const edge of gqlRegistries) {
    const registry = edge.node;
    const registryProjectName = registry?.name;
    const {registryName} = checkRegistryProject(registryProjectName);
    if (registry != null && registryName != null) {
      registriesInfo.push(
        getGlobalRegistryType(
          registry,
          registryName,
          registry.entityName,
          orgId,
          orgName
        )
      );
    }
  }
  return registriesInfo;
}

export type FetchRegistriesResult = {
  currentPage: globalRegistryType[];
  totalVisibleRegistriesLoaded: number; // Excludes starred registries
  loading: boolean; // Whether registries are still being fetched
  refetch: () => void;
  page: number;
  onPageChange: (page: number) => void;
  hasNextPage: boolean;
  error?: ApolloError;
};

type RegistriesHookProps = {
  orgName: string;
  pageSize: number;
  starredRegistryIds: Set<string>;
};

// Heavily inspired by useSearchablePaginatedRegistryArtifactCollections
export const FETCH_REGISTRIES_PAGE_SIZE = 100;
export const useSearchablePaginatedRegistries = ({
  orgName,
  pageSize,
  starredRegistryIds,
}: RegistriesHookProps): FetchRegistriesResult => {
  const getSearchResult = getAllRegistriesSearchResult;

  const {searchPattern} = useContext(RegistriesSearchContext);
  const [page, setPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<globalRegistryType[]>([]);

  const {refetch, fetchMore, data, loading, error} =
    useFetchRegistryProjectsPartialQuery({
      variables: {
        orgName,
        pattern: searchPattern,
        limit: FETCH_REGISTRIES_PAGE_SIZE,
      },
      onCompleted: data => {
        if (searchPattern) {
          Analytics.track('Registry search interaction', {
            action: 'search registries',
            entityName: orgName,
            searchTerm: searchPattern,
            numResults: getSearchResult(data)?.edges.length ?? 0,
          });
        }
      },
      context: propagateErrorsContext(),
      // This updates data whenever fetchMore is called and results in
      // duplicate queries being made, so we need to make sure we don't
      // append the same data twice in updateQuery
      notifyOnNetworkStatusChange: true,
    });

  const fetchMoreRegistries = useCallback(
    (endCursor: string) => {
      fetchMore({
        variables: {
          orgName,
          after: endCursor,
          pattern: searchPattern,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          if (searchPattern) {
            const totalNumResults = getSearchResult(prev)?.edges.length ?? 0;
            Analytics.track('Registry search interaction', {
              action: 'search registries next page',
              entityName: orgName,
              searchTerm: searchPattern,
              numResults: searchResult.edges.length,
              totalNumResults: totalNumResults,
            });
          }
          return produce(prev, draft => {
            // We only update the current page if the end cursor is different,
            // preventing duplicate data from being added to the current page
            const draftSearchResult = getSearchResult(draft);
            if (
              draftSearchResult &&
              draftSearchResult.pageInfo.endCursor !==
                searchResult.pageInfo?.endCursor
            ) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [fetchMore, orgName, searchPattern, getSearchResult]
  );

  const {edges, pageInfo} = useMemo(() => {
    return {
      edges: getSearchResult(data)?.edges ?? [],
      pageInfo: getSearchResult(data)?.pageInfo,
    };
  }, [data, getSearchResult]);

  const nonStarredEdges = useMemo(() => {
    if (!starredRegistryIds || starredRegistryIds.size === 0) {
      return edges;
    }
    return edges.filter(e => {
      const id = e.node?.id;
      return id && !starredRegistryIds.has(id);
    });
  }, [edges, starredRegistryIds]);

  // Clamp current page when the number of non-starred registries shrinks
  useEffect(() => {
    const totalItems = nonStarredEdges.length;
    const maxPage = Math.max(0, Math.ceil(totalItems / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [nonStarredEdges.length, page, pageSize]);

  useEffect(() => {
    if (loading || error) {
      return;
    }
    // check if we need to fetch more data for the current page
    if (
      nonStarredEdges.length < (page + 1) * pageSize &&
      pageInfo?.hasNextPage
    ) {
      fetchMoreRegistries(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more data, set the current page
      const orgId = data?.organization?.id ?? '';
      const currentRegistries: globalRegistryType[] =
        parseRegistriesSearchResult(
          orgName,
          orgId,
          sliceArray(nonStarredEdges, page, pageSize)
        );
      setCurrentPage(currentRegistries);
    }
  }, [
    nonStarredEdges,
    pageInfo,
    page,
    pageSize,
    fetchMoreRegistries,
    data?.organization?.id,
    orgName,
    loading,
    error,
  ]);

  // reset the view to the first page if the search filter changes
  useEffect(() => {
    setPage(0);
  }, [searchPattern]);

  return {
    currentPage: currentPage,
    loading: loading,
    refetch: refetch,
    totalVisibleRegistriesLoaded: nonStarredEdges.length,
    onPageChange: setPage,
    page: page,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    error: error,
  };
};

export type RegistryTypeWithMembership = globalRegistryType & {
  canViewCollectionCount: boolean;
  isRegistryAvailableToJoin: boolean;
};

type UseRegistriesWithMembershipProps = {
  orgName: string;
  registries: globalRegistryType[];
  viewerId?: string;
};

export const useRegistriesWithMembership = ({
  orgName,
  registries,
  viewerId,
}: UseRegistriesWithMembershipProps): {
  registriesWithMembership: RegistryTypeWithMembership[];
  loading: boolean;
  error?: ApolloError;
} => {
  const {viewerMemberRole} = useRegistryAccountSettings();
  // get list of restricted registries that need membership checks
  const restrictedProjectIDs = useMemo(
    () =>
      registries
        .filter(registry => registry.access === AccessOption.Restricted)
        .map(registry => registry.registryProjectID),
    [registries]
  );

  // fetch membership data for restricted registries
  const {
    data: membershipData,
    loading: membershipLoading,
    error,
  } = useBatchRegistryViewerMembershipQuery({
    variables: {
      entityName: orgName,
      projectIDs: restrictedProjectIDs,
      viewerID: viewerId ?? '',
    },
    context: propagateErrorsContext(),
    skip: restrictedProjectIDs.length === 0 || !viewerId,
  });

  // lookup map for membership data
  const membershipMap: Map<string, GQLRegistryProjectViewerMembershipType> =
    useMemo(() => {
      const map = new Map<string, any>();
      membershipData?.projects?.edges?.forEach(edge => {
        if (edge?.node) {
          map.set(edge.node.id, edge.node);
        }
      });
      return map;
    }, [membershipData]);

  const registriesWithMembership = useMemo(() => {
    return registries.map(registry => {
      // public registries, show collection count
      if (registry.access !== AccessOption.Restricted) {
        return {
          ...registry,
          canViewCollectionCount: true,
          isRegistryAvailableToJoin: false,
        };
      }

      if (membershipLoading) {
        return {
          ...registry,
          canViewCollectionCount: false,
          isRegistryAvailableToJoin: false,
        };
      }

      const membershipProject = membershipMap.get(registry.registryProjectID);
      if (!membershipProject) {
        return {
          ...registry,
          canViewCollectionCount: false,
          isRegistryAvailableToJoin:
            viewerMemberRole === OrganizationMemberRole.ADMIN,
        };
      }

      const isRegistryMember =
        isViewerRegistryMemberFromQuery(membershipProject);

      return {
        ...registry,
        canViewCollectionCount: isRegistryMember,
        isRegistryAvailableToJoin:
          !isRegistryMember &&
          viewerMemberRole === OrganizationMemberRole.ADMIN,
      };
    });
  }, [registries, membershipMap, membershipLoading, viewerMemberRole]);

  return {
    registriesWithMembership,
    loading: membershipLoading,
    error,
  };
};
